import {GridWidgetDataField} from './grid-widget-data-field';
import {RestArgument} from './rest-argument';
import {Persistable} from './persistable';

export interface ComparatorSetting {
  comparator: string;
  isVisible: boolean;
  messageKey: string;
}

export abstract class GridWidgetFilter implements Persistable {

  //Be mindful that this has a pointer to the dataField. If the dataField is removed
  //we need to be sure to remove this object as well.
  dataField: GridWidgetDataField;

  constructor(dataField: GridWidgetDataField) {
    this.dataField = dataField;
  }

  abstract isBlank(): boolean;

  abstract internalPassesFilter(dataFromDataRow: any): boolean;

  /**
   * Returns the tag name associated with the filter component that will be drawn.
   * If you have created a custom Angular component and are returning a class token from getProviderToken(),
   * then this can simply return an empty string from this method.
   */
  abstract getTagName(): string;

  /**
   * Shim to continue supporting Angular.
   * This could still be used in custom filters and it can be overridden to return actual GridWidgetFilter class token.
   */
  getProviderToken(): any {
    //Defaults to tag name, but can be overidden.
    return this.getTagName();
  }

  /**
   * Determines if the dataRow passes the "this" filter object.
   * @param dataRow - A javascrcript object that represents a row in the table
   * being wrapped by the grid widget.
   * @param defaultValue - boolean - The value to return in the case of parse issues
   * in the GridWidgetFilter object.
   * Insight: advanced filters use true because a parse error should make it act as if it's blank, which passes everything).
   *          simple filters pass in false because a parse error means that it did not pass the filter, and it's doing
   *          a logical OR on the results of all filters.
   *
   * @returns boolean - true if the value in dataRow passes the filter
   *                    false if the value in dataRow does not pass the filter
   */
  passesFilter(dataRow): boolean {
    if (this.isBlank()) {
      return true;
    }

    let dataFromDataRow = this.dataField.resolve(dataRow);
    if (dataFromDataRow instanceof Array) {
      if (dataFromDataRow.length === 0) {
        //You don't pass any of the filters if there is no data
        //to pass.
        return false;
      }

      return (<Array<any>>dataFromDataRow).find((dataItem) => {
          return this.internalPassesFilter(dataItem);
        }) != null;
    } else {
      return this.internalPassesFilter(dataFromDataRow);
    }
  }

  /**
   * This function will be called once. Then passesFilter will be called hundreds of times.
   * So this function can set up search regexes based on the filter criteria for the user.
   */
  abstract preFilterUpdate(): void;

  /**
   * Takes user selections from an existing filter and applies them to this instance of the filter.
   */
  useDataFromExistingFilter(existingFilter: GridWidgetFilter): void {
  }

  /**
   * Sets up the quick filter.
   *
   * @param text The thing to search for.
   * @returns true if the quick filter is valid for filtering.
   * Example  putting 'hello' into a text filter would return true.
   *          putting '456' into a number filter would return true.
   *          putting 'hello' into a number filter would return false.
   *          putting null, undefined, or '' into any filter should return false.
   */
  abstract setQuickSearchValue(text: string): boolean;

  /**
   * Returns an array of objects that describe what the filter is working on.
   * This is used for server side Sorting, Filtering, and Pagination.
   */
  abstract getRestArguments(): Array<RestArgument>;

  abstract getTagText(): string;

  abstract getPersistenceObject(): Object | undefined;

  abstract restoreFromPersistenceObject(value: Object): void;
}
